import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import { CheckCircle, AlertCircle, X, Search, Truck, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_CFG = {
  submitted:  { cls: "bg-blue-100 text-blue-700",       label: "Submitted" },
  approved:   { cls: "bg-emerald-100 text-emerald-700", label: "Approved" },
  in_transit: { cls: "bg-amber-100 text-amber-700",     label: "In Transit" },
  arrived:    { cls: "bg-violet-100 text-violet-700",   label: "Arrived" },
  validating: { cls: "bg-orange-100 text-orange-700",   label: "Validating" },
  validated:  { cls: "bg-teal-100 text-teal-700",       label: "Validated" },
  completed:  { cls: "bg-green-100 text-green-700",     label: "Completed" },
};

function Badge({ status }) {
  const cfg = STATUS_CFG[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function InboundReceiving() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [validating, setValidating] = useState(null); // id being validated
  const [actualQtys, setActualQtys] = useState({});
  const [receivingNotes, setReceivingNotes] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/inbound");
      // 3PL sees in-transit, arrived, validating
      const relevant = (res.data.shipments || []).filter(s =>
        ["in_transit","arrived","validating","validated","completed"].includes(s.status)
      );
      setShipments(relevant);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markArrived = async (id) => {
    try {
      await api.patch(`/api/inbound/${id}/status`, { status: "arrived" });
      flash("Marked as Arrived at warehouse.");
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  const startValidating = (shipment) => {
    setValidating(shipment.id);
    const qtys = {};
    const notes = {};
    (shipment.items || []).forEach((item, i) => {
      qtys[i] = item.qty;
      notes[i] = "";
    });
    setActualQtys(qtys);
    setReceivingNotes(notes);
  };

  const submitValidation = async (shipment) => {
    setSaving(true);
    try {
      const items = (shipment.items || []).map((item, i) => ({
        actualQty: parseInt(actualQtys[i]) || 0,
        receivingNotes: receivingNotes[i] || "",
      }));
      await api.patch(`/api/inbound/${shipment.id}/status`, { status: "validated", items });
      flash("Validation complete. Inventory has been updated.");
      setValidating(null);
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const filtered = shipments.filter(s =>
    s.asnNumber?.toLowerCase().includes(search.toLowerCase()) ||
    s.companyId?.toLowerCase().includes(search.toLowerCase()) ||
    s.vendorName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-amber-100 rounded-xl"><Truck size={24} className="text-amber-600"/></div>
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Inbound Receiving</h1>
          <p className="text-slate-500 text-sm">Validate incoming goods and update inventory</p>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400"/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ASN, company, or vendor..."
          className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"/>
        {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center text-slate-400 text-sm">
            No inbound shipments to receive.
          </div>
        ) : filtered.map(s => (
          <div key={s.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="text-slate-400 hover:text-slate-600">
                  {expanded === s.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                </button>
                <div>
                  <p className="font-bold text-navy-900 font-mono">{s.asnNumber}</p>
                  <p className="text-xs text-slate-400">From: {s.vendorName} · To: {s.companyId}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{s.items?.length || 0} items</span>
                <Badge status={s.status}/>
                {s.status === "in_transit" && (
                  <button onClick={() => markArrived(s.id)}
                    className="px-3 py-1.5 bg-violet-600 text-white text-xs font-bold rounded-lg hover:bg-violet-700">
                    Mark Arrived
                  </button>
                )}
                {(s.status === "arrived" || s.status === "validating") && validating !== s.id && (
                  <button onClick={() => startValidating(s)}
                    className="px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-700">
                    Validate Receipt
                  </button>
                )}
              </div>
            </div>

            {/* Validation form */}
            {validating === s.id && (
              <div className="border-t border-slate-200 bg-amber-50 px-6 py-5">
                <h4 className="font-bold text-navy-900 mb-3 text-sm">Validate Quantities — Enter actual received qty for each item</h4>
                <div className="space-y-2 mb-4">
                  {(s.items || []).map((item, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white rounded-xl p-3 border border-slate-200">
                      <div className="col-span-4">
                        <p className="font-semibold text-sm text-navy-900">{item.description}</p>
                        <p className="text-xs text-slate-400 font-mono">{item.sku}</p>
                      </div>
                      <div className="col-span-2 text-center">
                        <p className="text-[10px] text-slate-400 uppercase font-bold">Expected</p>
                        <p className="font-bold text-navy-900">{item.qty}</p>
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Actual Qty *</label>
                        <input type="number" min="0" value={actualQtys[i] ?? item.qty}
                          onChange={e => setActualQtys(q => ({...q, [i]: e.target.value}))}
                          className={`w-full border rounded-lg px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-teal-300 ${
                            parseInt(actualQtys[i]) !== item.qty ? "border-red-300 bg-red-50" : "border-slate-200"
                          }`}/>
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Notes</label>
                        <input value={receivingNotes[i] || ""} onChange={e => setReceivingNotes(n => ({...n, [i]: e.target.value}))}
                          placeholder="Damage, short, etc."
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-300"/>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setValidating(null)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button onClick={() => submitValidation(s)} disabled={saving}
                    className="px-5 py-2 rounded-xl bg-teal-600 text-white text-sm font-bold hover:bg-teal-700 disabled:opacity-50">
                    {saving ? "Saving..." : "✓ Confirm & Update Inventory"}
                  </button>
                </div>
              </div>
            )}

            {/* Expanded view */}
            {expanded === s.id && validating !== s.id && (
              <div className="border-t border-slate-100 px-6 py-4 bg-slate-50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <th className="text-left pb-2">SKU</th>
                      <th className="text-left pb-2">Description</th>
                      <th className="text-right pb-2">Expected</th>
                      {s.status === "validated" || s.status === "completed" ? <>
                        <th className="text-right pb-2">Actual</th>
                        <th className="text-right pb-2">Diff</th>
                      </> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(s.items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 font-mono text-xs text-slate-500">{item.sku || "—"}</td>
                        <td className="py-2 text-slate-700">{item.description}</td>
                        <td className="py-2 text-right text-slate-600">{item.qty}</td>
                        {(s.status === "validated" || s.status === "completed") && <>
                          <td className="py-2 text-right font-semibold text-navy-900">{item.actualQty ?? "—"}</td>
                          <td className={`py-2 text-right font-bold text-xs ${item.discrepancy > 0 ? "text-blue-600" : item.discrepancy < 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {item.discrepancy != null ? (item.discrepancy > 0 ? `+${item.discrepancy}` : item.discrepancy) : "—"}
                          </td>
                        </>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
